import { useTranslation } from 'react-i18next'
import { Briefcase, Globe, User, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ClientType } from '@/types'

const TYPE_OPTIONS: {
  value: ClientType
  icon: LucideIcon
  solidBg: string
  lightBg: string
  iconMuted: string
  labelKey: string
  descKey: string
}[] = [
  {
    value: 'individual',
    icon: User,
    solidBg: 'bg-[#2550cc]',
    lightBg: 'bg-[#ebf1fe]',
    iconMuted: 'text-[#2550cc]',
    labelKey: 'form.individual',
    descKey: 'form.individualDesc',
  },
  {
    value: 'company',
    icon: Briefcase,
    solidBg: 'bg-[#722ed1]',
    lightBg: 'bg-[#efdbff]',
    iconMuted: 'text-[#722ed1]',
    labelKey: 'form.business',
    descKey: 'form.businessDesc',
  },
  {
    value: 'property_management',
    icon: Globe,
    solidBg: 'bg-[#ca8a04]',
    lightBg: 'bg-[#fbf3d9]',
    iconMuted: 'text-[#ca8a04]',
    labelKey: 'form.propertyManager',
    descKey: 'form.propertyManagerDesc',
  },
]

interface ClientTypePickerProps {
  value: ClientType
  onChange: (type: ClientType) => void
}

/** Figma type tiles: horizontal icon + title + description + radio. */
export function ClientTypePicker({ value, onChange }: ClientTypePickerProps) {
  const { t } = useTranslation('clients')

  return (
    <div className='flex flex-col gap-3 sm:flex-row sm:items-stretch'>
      {TYPE_OPTIONS.map(
        ({
          value: option,
          icon: Icon,
          solidBg,
          lightBg,
          iconMuted,
          labelKey,
          descKey,
        }) => {
          const active = value === option
          return (
            <button
              key={option}
              type='button'
              onClick={() => onChange(option)}
              aria-pressed={active}
              className={cn(
                'flex min-w-0 flex-1 gap-2 rounded-xl p-3 text-left transition-colors',
                active
                  ? 'border-2 border-[#059669] bg-[#ecfdf5]'
                  : 'border border-[#e7e9ef] bg-white hover:bg-[#fbfbfc]'
              )}
            >
              <span className='flex min-w-0 flex-1 gap-2 items-start'>
                <span
                  className={cn(
                    'flex size-10 shrink-0 items-center justify-center rounded-xl',
                    active ? solidBg : lightBg
                  )}
                >
                  <Icon
                    className={cn('size-5', active ? 'text-white' : iconMuted)}
                  />
                </span>
                <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                  <span className='text-xs font-medium normal-case leading-4 text-[#171a20]'>
                    {t(labelKey)}
                  </span>
                  <span className='text-[10px] font-semibold normal-case leading-4 text-[#6c7285]'>
                    {t(descKey)}
                  </span>
                </span>
              </span>
              <span
                className={cn(
                  'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full',
                  active ? 'bg-[#10b981]' : 'border border-[#b0b5c3] bg-white'
                )}
                aria-hidden='true'
              >
                {active && <span className='size-2 rounded-full bg-white' />}
              </span>
            </button>
          )
        }
      )}
    </div>
  )
}
